import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InfiniteScrollTrigger from "components/infinite-scroll/InfiniteScrollTrigger";
import PredictionCard from "components/card/PredictionCard";
import { skeletonGenerator } from "components/infinite-scroll/helpers";
import PredictionSkeleton from "components/infinite-scroll/skeletons/PredictionSkeleton";
import ListHeaderToolbar from "components/common/ListHeaderToolbar";
import { getPredictions } from "api-algolia/predictions.api";
import useSearch from "hooks/algolia/useSearch";
import { FiTrendingUp, FiInbox, FiRotateCcw, FiDownloadCloud } from "react-icons/fi";

const DEFAULT_CATEGORIES = [
  { label: "Քաղաքական", value: "politic" },
  { label: "Սոցիալական", value: "social" },
  { label: "Տնտեսական", value: "economic" },
  { label: "Ֆինանսական", value: "finance" },
  { label: "Սպորտային", value: "sport" },
  { label: "Գիտական", value: "scientific" },
  { label: "Մարգարեական", value: "prophetic" },
];

const CATEGORY_MAP = {
  politic: ["politic", "politics", "քաղաքական", "քաղաքականություն"],
  social: ["social", "society", "սոցիալական", "հասարակություն", "հասարակական"],
  economic: ["economic", "economy", "տնտեսական", "տնտեսություն"],
  finance: ["finance", "financial", "ֆինանսական", "ֆինանսներ"],
  sport: ["sport", "sports", "սպորտ", "սպորտային"],
  scientific: ["scientific", "science", "գիտական", "գիտություն"],
  prophetic: ["prophetic", "մարգարեական", "մարգարեություն"],
};

const parseTimestamp = (val) => {
  if (!val) return 0;
  if (typeof val === "number") return val;
  if (typeof val?.toMillis === "function") return val.toMillis();
  if (typeof val?.toDate === "function") return val.toDate().getTime();
  if (typeof val?._seconds === "number") {
    return val._seconds * 1000 + Math.round((val._nanoseconds || 0) / 1e6);
  }
  if (typeof val?.seconds === "number") {
    return val.seconds * 1000 + Math.round((val.nanoseconds || 0) / 1e6);
  }
  const parsed = Date.parse(val);
  return isNaN(parsed) ? 0 : parsed;
};

const PredictionsList = ({
  predictions = [],
  totalCount,
  counts,
  currentUserId,
  currentUserDisplayName,
  currentUserType,
  isLoadingNextPage,
  hasMoreItems,
  getNextPage,
  page,
  setPage,
  createPrediction,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 1. Search Hook
  const { setSearchText, searchText, searchResult, searchLoading } = useSearch({
    getData: getPredictions,
  });

  // 2. Filter States
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "finished", "pending"
  const [outcomeFilter, setOutcomeFilter] = useState("all"); // "all", "true", "false"
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest"); // "newest", "oldest", "deadline"

  const isSearching = searchText.trim().length > 0;
  const baseItems = useMemo(
    () => (isSearching ? searchResult || [] : predictions),
    [isSearching, searchResult, predictions]
  );

  // 3. Dynamic Categories detection
  const categoryOptions = useMemo(() => {
    const catsMap = new Map();
    DEFAULT_CATEGORIES.forEach((c) => catsMap.set(c.value, c.label));

    // Scan loaded items for any additional categories
    predictions.forEach((item) => {
      const cats = Array.isArray(item.categories)
        ? item.categories
        : [item.category].filter(Boolean);
      cats.forEach((cat) => {
        if (typeof cat === "string" && !catsMap.has(cat)) {
          catsMap.set(cat, cat);
        }
      });
    });

    return Array.from(catsMap.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [predictions]);

  // 4. Metrics & Real Counts
  const stats = useMemo(() => {
    let localActive = 0;
    let localFinished = 0;
    let localPending = 0;

    predictions.forEach((p) => {
      if (p.type === "finished") localFinished++;
      else if (p.type === "pending") localPending++;
      else localActive++;
    });

    return {
      total: counts?.total || totalCount || predictions.length,
      active: counts?.active ?? localActive,
      finished: counts?.finished ?? localFinished,
      pending: counts?.pending ?? localPending,
    };
  }, [predictions, counts, totalCount]);

  // 5. Apply Filters and Sorting
  const filteredPredictions = useMemo(() => {
    let result = [...baseItems];

    // Status filter
    if (statusFilter === "active") {
      result = result.filter(
        (p) => p.type !== "finished" && p.type !== "pending"
      );
    } else if (statusFilter === "finished") {
      result = result.filter((p) => p.type === "finished");
    } else if (statusFilter === "pending") {
      result = result.filter((p) => p.type === "pending");
    }

    // Outcome filter (applicable to finished items)
    if (outcomeFilter === "true") {
      result = result.filter((p) => p.result === true);
    } else if (outcomeFilter === "false") {
      result = result.filter((p) => p.result === false);
    }

    // Category filter
    if (categoryFilter !== "all") {
      const targetTerms =
        CATEGORY_MAP[categoryFilter.toLowerCase()] || [
          categoryFilter.toLowerCase(),
        ];
      result = result.filter((p) => {
        const cats = Array.isArray(p.categories)
          ? p.categories
          : [p.category].filter(Boolean);

        return cats.some((cat) => {
          const cLower = String(cat).toLowerCase().trim();
          return targetTerms.some(
            (term) => cLower === term || cLower.includes(term)
          );
        });
      });
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "oldest") {
        return parseTimestamp(a.createdAt) - parseTimestamp(b.createdAt);
      }
      if (sortBy === "deadline") {
        const da = parseTimestamp(a.deadline || a.endOfVoting);
        const db = parseTimestamp(b.deadline || b.endOfVoting);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da - db;
      }
      // default: newest
      return parseTimestamp(b.createdAt) - parseTimestamp(a.createdAt);
    });

    return result;
  }, [baseItems, statusFilter, outcomeFilter, categoryFilter, sortBy]);

  const hasActiveFilters =
    isSearching ||
    statusFilter !== "all" ||
    outcomeFilter !== "all" ||
    categoryFilter !== "all" ||
    sortBy !== "newest";

  const handleResetFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setOutcomeFilter("all");
    setCategoryFilter("all");
    setSortBy("newest");
  };

  const handleCreate = async () => {
    const prediction = await createPrediction({
      createdAt: Date.now(),
      author: {
        id: currentUserId,
        displayName: currentUserDisplayName,
      },
    });
    navigate(`/predictions/manage/${prediction.id}`);
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 p-6 sm:p-8 lg:p-10 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Enterprise Header & Filter Toolbar */}
        <ListHeaderToolbar
          title="Կանխատեսումներ"
          icon={FiTrendingUp}
          totalCount={stats.total}
          activeCount={stats.active}
          finishedCount={stats.finished}
          pendingCount={stats.pending}
          // Search
          searchText={searchText}
          setSearchText={setSearchText}
          searchPlaceholder="Որոնել կանխատեսումներ ըստ վերնագրի կամ հեղինակի..."
          // Status
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          statusOptions={[
            { id: "all", label: "Բոլորը" },
            { id: "active", label: "Ակտիվ" },
            { id: "finished", label: "Ավարտված" },
            { id: "pending", label: "Սևագիր" },
          ]}
          // Outcome
          outcomeFilter={outcomeFilter}
          setOutcomeFilter={setOutcomeFilter}
          outcomeOptions={[
            { value: "all", label: "Բոլոր արդյունքները" },
            { value: "true", label: "Ճիշտ է (Իրականացած)" },
            { value: "false", label: "Սխալ է (Չիրականացած)" },
          ]}
          // Category
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          categoryOptions={categoryOptions}
          // Sort
          sortBy={sortBy}
          setSortBy={setSortBy}
          // Create Button
          canCreate={currentUserType === "admin" || currentUserType === "editor"}
          onCreateNew={handleCreate}
          createButtonText="Նոր կանխատեսում"
          // Reset
          hasActiveFilters={hasActiveFilters}
          onResetFilters={handleResetFilters}
          filteredCount={filteredPredictions.length}
        />

        {/* Content Section */}
        {searchLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5 w-full">
            {skeletonGenerator(<PredictionSkeleton />)}
          </div>
        ) : filteredPredictions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5 w-full">
            {filteredPredictions.map((prediction, index) => (
              <PredictionCard
                showTimers
                key={prediction.id || index}
                item={prediction}
                primaryButtonText={t("card.view")}
                secondaryButtonText={t("card.edit")}
                isDisabled={prediction.type === "finished"}
                onCardClick={() =>
                  navigate(`/predictions/manage/${prediction.id}`)
                }
              />
            ))}
          </div>
        ) : (
          /* Modern Empty State */
          <div className="bg-white border border-slate-200/80 rounded-2xl p-12 text-center max-w-lg mx-auto my-8 shadow-xs">
            <span className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center mx-auto mb-4">
              <FiInbox size={26} />
            </span>
            <h3 className="text-base font-bold text-slate-900 mb-1">
              Կանխատեսումներ չեն գտնվել
            </h3>
            <p className="text-xs text-slate-500 mb-5 leading-relaxed">
              {hasActiveFilters
                ? "Նշված ֆիլտրերին կամ որոնման հարցմանը համապատասխանող արդյունքներ չկան ներբեռնված ցանկում:"
                : "Համակարգում դեռևս չկան ավելացված կանխատեսումներ:"}
            </p>
            <div className="flex items-center justify-center gap-3">
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition cursor-pointer"
                >
                  <FiRotateCcw size={13} />
                  Մաքրել ֆիլտրերը
                </button>
              )}
              {hasMoreItems && !isSearching && (
                <button
                  type="button"
                  onClick={() => getNextPage && getNextPage()}
                  disabled={isLoadingNextPage}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition cursor-pointer disabled:opacity-50"
                >
                  <FiDownloadCloud size={13} />
                  {isLoadingNextPage ? "Բեռնվում է..." : "Բեռնել ավելին սերվերից"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Load More Button & Infinite Scroll */}
        {!isSearching && (
          <div className="mt-4 flex flex-col items-center justify-center gap-3">
            {isLoadingNextPage && (
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5 w-full">
                {skeletonGenerator(<PredictionSkeleton />)}
              </div>
            )}

            {hasMoreItems && !isLoadingNextPage && (
              <button
                type="button"
                onClick={() => getNextPage && getNextPage()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold shadow-xs transition hover:shadow-sm cursor-pointer"
              >
                <FiDownloadCloud size={15} />
                Բեռնել հաջորդ էջը
              </button>
            )}

            <InfiniteScrollTrigger
              isLoading={isLoadingNextPage}
              hasMoreItems={hasMoreItems}
              callback={getNextPage}
              page={page}
              setPage={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictionsList;
